<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiLink, getUrgencyLabel } from '@shopexperts/shared';
import type { LeadClientResponse } from '@shopexperts/shared';

interface ClientLeadCardProps {
    lead: LeadClientResponse;
}

const props = defineProps<ClientLeadCardProps>();

// Format date
const formattedDate = computed(() => {
    const date = new Date(props.lead.created_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

// Status badge color
const statusColor = computed(() => {
    return props.lead.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';
});

// Status label
const statusLabel = computed(() => {
    return props.lead.status === 'pending' ? 'Awaiting Match' : 'Matched';
});

// Urgency badge color
const urgencyColor = computed(() => {
    switch (props.lead.urgency) {
        case 'urgent':
        case 'as_soon_as_possible':
            return 'bg-red-100 text-red-800';
        case 'within_the_next_week':
            return 'bg-orange-100 text-orange-800';
        case 'within_the_next_month':
            return 'bg-yellow-100 text-yellow-800';
        case 'standard':
        case 'open_to_expert_opinion':
        default:
            return 'bg-blue-100 text-blue-800';
    }
});

// Urgency display label
const urgencyLabel = computed(() => getUrgencyLabel(props.lead.urgency));

// Check if an expert is the preferred/selected expert
const isPreferredExpert = (expertId: number) => {
    return props.lead.selected_expert?.id === expertId;
};

</script>

<template>
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div class="flex flex-col lg:flex-row gap-6 items-stretch">
            <!-- Left Column: Project Details -->
            <div class="flex-1 flex flex-col min-w-0">
                <!-- Project Title -->
                <div class="mb-3">
                    <div class="flex items-center gap-2 flex-wrap">
                        <UiText as="span" variant="p-small" class="text-gray-400">#{{ lead.id }}</UiText>
                        <UiText as="h4" variant="h5" class="font-semibold">
                            {{ lead.project_title }}
                        </UiText>
                    </div>
                    <UiLink 
                        :href="lead.store.url" 
                        external="with-arrow" 
                        variant="p-small"
                        :label="lead.store.title" 
                    />
                </div>

                <!-- Project Brief -->
                <UiText 
                    v-if="lead.project_brief" 
                    as="p" 
                    variant="p-small" 
                    class="text-gray-600 mb-3"
                    :text="lead.project_brief" 
                    :truncate="3" 
                    :show-more-button="true" 
                />

                <div class="flex-1"></div>

                <!-- Metadata Row -->
                <div class="flex gap-4 text-xs text-gray-600">
                    <div>
                        <UiText as="span" variant="caption-normal" class="text-gray-500">Budget: </UiText>
                        <UiText as="span" variant="caption-normal" class="font-semibold">{{ lead.budget_range }}</UiText>
                    </div>
                    <div>
                        <UiText as="span" variant="caption-normal" class="text-gray-500">Created: </UiText>
                        <UiText as="span" variant="caption-normal" class="font-semibold">{{ formattedDate }}</UiText>
                    </div>
                </div>
            </div>

            <!-- Right Column: Status & Matches -->
            <div class="w-full lg:w-72 flex-shrink-0 flex flex-col gap-3">
                <!-- Status Badges -->
                <div class="flex gap-2 justify-start lg:justify-end flex-wrap">
                    <span :class="urgencyColor" class="px-2 py-0.5 rounded text-xs font-medium">
                        {{ urgencyLabel }}
                    </span>
                    <span :class="statusColor" class="px-2 py-0.5 rounded text-xs font-medium">
                        {{ statusLabel }}
                    </span>
                </div>

                <!-- Matched Experts List -->
                <div v-if="lead.matches.length > 0" class="flex-1 min-h-0">
                    <UiText as="p" variant="p-small" class="text-gray-600 text-xs mb-2">
                        Matched Experts ({{ lead.matches.length }})
                    </UiText>
                    <div class="space-y-1.5 max-h-32 overflow-y-auto">
                        <div 
                            v-for="match in lead.matches" 
                            :key="match.id"
                            class="flex items-center gap-2 p-2 rounded text-xs"
                            :class="isPreferredExpert(match.expert.id) ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'"
                        >
                            <div v-if="match.expert.avatar_media" class="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                <img 
                                    :src="match.expert.avatar_media.cdn_url" 
                                    :alt="match.expert.name"
                                    class="w-full h-full object-cover" 
                                />
                            </div>
                            <div v-else class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <span class="text-gray-500 text-[10px] font-medium">
                                    {{ match.expert.name.charAt(0).toUpperCase() }}
                                </span>
                            </div>
                            <div class="flex-1 min-w-0">
                                <span class="font-semibold truncate block">{{ match.expert.name }}</span>
                            </div>
                            <span 
                                v-if="isPreferredExpert(match.expert.id)" 
                                class="text-blue-600 text-[10px] font-medium"
                            >
                                Preferred
                            </span>
                            <span v-else class="text-gray-400 capitalize text-[10px]">{{ match.expert.type }}</span>
                        </div>
                    </div>
                </div>

                <!-- Empty state when no matches yet -->
                <div v-else-if="lead.status === 'pending'" class="flex-1 flex items-center justify-center p-4 bg-gray-50 rounded-md">
                    <UiText as="p" variant="p-small" class="text-gray-500 text-center text-xs">
                        We're finding the best experts for your project...
                    </UiText>
                </div>
            </div>
        </div>
    </div>
</template>

